(() => {
  if (window.__kindVeilCartBundleQuantityReady) return;
  window.__kindVeilCartBundleQuantityReady = true;

  const pendingMarkers = new Set();

  const bundleControls = (marker) =>
    [...document.querySelectorAll('[data-kv-cart-bundle-quantity]')].filter(
      (control) => control.dataset.kvBundleMarker === marker
    );

  const bundleRemoveButtons = (marker) =>
    [...document.querySelectorAll('[data-kv-bundle-remove]')].filter(
      (button) => button.dataset.kvBundleMarker === marker
    );

  const setBusy = (marker, busy) => {
    bundleControls(marker).forEach((control) => {
      control.classList.toggle('is-loading', busy);
      control.setAttribute('aria-busy', busy ? 'true' : 'false');
      control.querySelectorAll('button').forEach((button) => {
        button.disabled = busy || (button.hasAttribute('data-kv-bundle-decrease') && Number(control.dataset.kvBundleCount) <= 1);
      });
    });
    bundleRemoveButtons(marker).forEach((button) => {
      button.classList.toggle('is-loading', busy);
      button.disabled = busy;
    });
  };

  const setStatus = (marker, message) => {
    bundleControls(marker).forEach((control) => {
      const status = control.querySelector('[data-kv-bundle-status]');
      if (status) status.textContent = message;
    });
  };

  const readJson = async (response, fallbackMessage) => {
    const raw = await response.text();
    let data;
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch (_) {
      throw new Error(fallbackMessage);
    }
    if (!response.ok || data.errors) {
      throw new Error(data.description || data.message || data.errors || fallbackMessage);
    }
    return data;
  };

  const normalizedCart = (cart) => ({
    id: cart.token || 'cart',
    totalQuantity: Number(cart.item_count || 0),
    cost: {
      totalAmount: {
        amount: (Number(cart.total_price || 0) / 100).toFixed(2),
        currencyCode: cart.currency || window.Shopify?.currency?.active || 'USD',
      },
    },
    lines: (cart.items || []).map((item) => ({
      id: item.key || item.id,
      quantity: Number(item.quantity || 0),
      cost: {
        totalAmount: {
          amount: (Number(item.final_line_price || 0) / 100).toFixed(2),
          currencyCode: cart.currency || window.Shopify?.currency?.active || 'USD',
        },
      },
    })),
    discountCodes: (cart.cart_level_discount_applications || []).map((discount) => ({
      applicable: true,
      code: discount.title || '',
    })),
  });

  const createCartEvent = (lines, action) => {
    let resolve;
    let reject;
    const promise = new Promise((success, failure) => {
      resolve = success;
      reject = failure;
    });
    const event = new Event('shopify:cart:lines-update', { bubbles: true });
    Object.assign(event, { action, context: 'cart', lines, promise });
    document.dispatchEvent(event);
    return { resolve, reject };
  };

  const updateBundleQuantity = async (marker, currentCount, nextCount) => {
    if (!marker || pendingMarkers.has(marker)) return;
    if (!Number.isInteger(currentCount) || currentCount < 1) return;
    nextCount = Math.max(0, Math.min(99, Number(nextCount) || 0));
    if (nextCount === currentCount) return;

    pendingMarkers.add(marker);
    setStatus(marker, '');
    setBusy(marker, true);

    let deferred;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = new URL('cart.js', `${window.location.origin}${root}`).href;
      const updateUrl = new URL('cart/update.js', `${window.location.origin}${root}`).href;
      const cart = await readJson(
        await fetch(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' }),
        'Unable to read the cart.'
      );
      const groupLines = (cart.items || []).filter(
        (item) => String(item.properties?._kind_veil_bundle || '') === marker
      );
      if (!groupLines.length) throw new Error('This bundle is no longer in the cart.');

      const updates = {};
      const eventLines = [];
      groupLines.forEach((item) => {
        const baseQuantity = Number(item.quantity || 0) / currentCount;
        if (!Number.isInteger(baseQuantity) || baseQuantity < 1) {
          throw new Error('Unable to keep this bundle synchronized. Please refresh the page.');
        }
        const quantity = baseQuantity * nextCount;
        updates[item.key] = quantity;
        eventLines.push({ id: item.key, quantity });
      });

      deferred = createCartEvent(eventLines, nextCount === 0 ? 'remove' : 'update');
      const sectionIds = [...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      )];
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const updatedCart = await readJson(response, 'Unable to update the bundle quantity.');

      deferred.resolve({
        cart: normalizedCart(updatedCart),
        detail: {
          sections: updatedCart.sections || {},
          items: updatedCart.items || [],
          itemCount: Number(updatedCart.item_count || 0),
          source: 'kind-veil-bundle-quantity',
          didError: false,
        },
      });

      if (!sectionIds.length) window.location.reload();
    } catch (error) {
      deferred?.reject(error);
      setStatus(marker, error?.message || 'Unable to update the bundle quantity.');
    } finally {
      pendingMarkers.delete(marker);
      setBusy(marker, false);
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const remove = event.target.closest('[data-kv-bundle-remove]');
    if (remove) {
      event.preventDefault();
      const marker = remove.dataset.kvBundleMarker || '';
      const control = bundleControls(marker)[0];
      const currentCount = Number(control?.dataset.kvBundleCount || 1);
      updateBundleQuantity(marker, currentCount, 0);
      return;
    }

    const decrease = event.target.closest('[data-kv-bundle-decrease]');
    const increase = event.target.closest('[data-kv-bundle-increase]');
    if (!decrease && !increase) return;

    event.preventDefault();
    const control = event.target.closest('[data-kv-cart-bundle-quantity]');
    const marker = control?.dataset.kvBundleMarker || '';
    const currentCount = Number(control?.dataset.kvBundleCount || 1);
    updateBundleQuantity(marker, currentCount, increase ? currentCount + 1 : currentCount - 1);
  });
})();
